// @ts-nocheck
/*
 * Survey engine.
 *
 * Drives the "Survey" section: shows one step at a time, follows the branching
 * rules declared on each step/choice, keeps a running score and picks the
 * matching result screen at the end.
 *
 * All content and logic is server rendered by the section's blocks, so the
 * marketing team edits everything from the theme editor. This file only reads
 * data attributes:
 *
 *   [data-step]        data-step-id, data-step-type, data-next, data-key
 *   [data-choice]      data-choice-id, data-points, data-next, data-value
 *   [data-step-type="result"]  data-min, data-max, data-tag
 */

(function () {
  const FOCUS_DELAY = 60;

  // Klaviyo pins its API by date. Bumping this is a deliberate upgrade —
  // check their changelog for breaking changes first.
  const KLAVIYO_REVISION = '2026-07-15';

  class SurveyForm extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.flow = this.steps.filter((step) => step.dataset.stepType !== 'result');
      this.results = this.steps.filter((step) => step.dataset.stepType === 'result');
      this.questions = this.flow.filter((step) => step.dataset.stepType === 'question');

      this.progressBar = this.querySelector('[data-survey-progress-bar]');
      this.progress = this.querySelector('[data-survey-progress]');
      this.stepsContainer = this.querySelector('[data-survey-steps]');
      this.backButton = this.querySelector('[data-survey-back]');

      this.answers = {};
      this.history = [];
      this.current = null;
      this.started = false;

      if (!this.flow.length) return;

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('keydown', this.handleShortcut);

      if (this.backButton) {
        this.backButton.addEventListener('click', () => this.back());
      }

      this.querySelectorAll('[data-survey-consent]').forEach((consent) => {
        const step = consent.closest('[data-step]');
        consent.addEventListener('change', () => this.updateConsentGate(step));
        this.updateConsentGate(step);
      });

      this.setupDesignMode();

      const restored = this.restore();
      if (!restored) this.show(this.flow[0], { silent: true });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleShortcut);
    }

    /* ---------------------------------------------------------------- state */

    get storageKey() {
      return `survey:${this.dataset.surveyId}`;
    }

    get remembers() {
      return this.dataset.remember === 'true';
    }

    get score() {
      return Object.keys(this.answers).reduce((total, key) => total + this.answers[key].points, 0);
    }

    save() {
      if (!this.remembers || !this.current) return;
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({
            answers: this.answers,
            history: this.history,
            stepId: this.current.dataset.stepId,
          })
        );
      } catch (error) {
        // Private browsing, storage disabled — carry on without persistence.
      }
    }

    /**
     * Pick up where the visitor left off. This also covers the theme editor,
     * which reloads the preview on every change — without it, testing the
     * survey restarts from step one each time.
     */
    restore() {
      if (!this.remembers) return false;
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch (error) {
        return false;
      }
      if (!saved || !saved.stepId) return false;

      const step = this.stepById(saved.stepId);
      if (!step) return false;

      this.answers = saved.answers || {};
      this.history = saved.history || [];
      this.started = true;
      Object.keys(this.answers).forEach((key) => this.markAnswered(key));
      this.show(step, { silent: true });
      return true;
    }

    /* ------------------------------------------------------------ navigation */

    stepById(id) {
      if (!id) return null;
      return this.steps.find((step) => step.dataset.stepId === id) || null;
    }

    show(step, options = {}) {
      if (!step) return;

      this.steps.forEach((item) => {
        const active = item === step;
        item.hidden = !active;
        item.classList.toggle('survey__step--active', active);
      });

      this.current = step;
      this.updateChrome();
      if (step.dataset.stepType === 'contact') this.updateConsentGate(step);
      this.save();

      if (!options.silent) {
        const heading = step.querySelector('[data-survey-focus]') || step;
        setTimeout(() => {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }, FOCUS_DELAY);

        const top = this.getBoundingClientRect().top + window.scrollY - 80;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }

      this.emit('step_view', {
        step_id: step.dataset.stepId,
        step_type: step.dataset.stepType,
        step_title: (step.dataset.title || '').slice(0, 120),
        step_number: this.flow.indexOf(step) + 1,
      });
    }

    updateChrome() {
      const step = this.current;
      if (!step) return;

      const hideProgress = step.dataset.hideProgress === 'true' || step.dataset.stepType === 'result';
      if (this.progress) this.progress.hidden = hideProgress;
      // Make up the space the bar would take whenever it isn't showing.
      if (this.stepsContainer) {
        this.stepsContainer.classList.toggle('survey-padding', !this.progress || hideProgress);
      }

      if (this.progressBar && this.questions.length) {
        const answered = Object.keys(this.answers).length;
        const percent = Math.round((answered / this.questions.length) * 100);
        this.progressBar.style.setProperty('--survey-progress', `${percent}%`);
        if (this.progress) this.progress.setAttribute('aria-valuenow', String(percent));
      }

      if (this.backButton) {
        this.backButton.hidden = this.history.length === 0 || step.dataset.stepType === 'result';
      }
    }

    /**
     * Resolve where a step leads: the chosen answer's target wins, then the
     * step's own target, then simply the next step in the editor's order.
     */
    resolveNext(step, choice) {
      const explicit = (choice && choice.dataset.next) || step.dataset.next;

      if (explicit) {
        const target = this.stepById(explicit);
        if (target) return target;
        console.warn(`[survey] Step "${explicit}" not found — falling through to the next step.`);
      }

      const index = this.flow.indexOf(step);
      return index > -1 ? this.flow[index + 1] : null;
    }

    advance(step, choice) {
      const next = this.resolveNext(step, choice);

      if (!next) {
        this.finish();
        return;
      }

      this.history.push(step.dataset.stepId);
      this.show(next);
    }

    back() {
      const previousId = this.history.pop();
      const previous = this.stepById(previousId);
      if (!previous) return;

      if (previous.dataset.stepType === 'question' && previous.dataset.key) {
        delete this.answers[previous.dataset.key];
      }

      this.show(previous);
    }

    /**
     * Hold the submit button closed until required consent is given, so the
     * tick box is visibly the thing that unlocks the results. Submitting by
     * other means still validates, so this is an affordance, not the guard.
     */
    updateConsentGate(step) {
      const button = step?.querySelector('[data-survey-submit]');
      if (!button) return;

      const consent = step.querySelector('[data-survey-consent]');
      button.disabled = Boolean(consent && consent.required && !consent.checked);
    }

    /* --------------------------------------------------------------- answers */

    markAnswered(key) {
      const answer = this.answers[key];
      if (!answer) return;
      const step = this.flow.find((item) => item.dataset.key === key);
      if (!step) return;

      step.querySelectorAll('[data-choice]').forEach((choice) => {
        const selected = choice.dataset.choiceId === answer.choice_id;
        choice.setAttribute('aria-checked', selected ? 'true' : 'false');
        choice.classList.toggle('survey-choice--selected', selected);
      });
    }

    select(choice) {
      const step = choice.closest('[data-step]');
      if (!step) return;

      step.querySelectorAll('[data-choice]').forEach((item) => {
        const selected = item === choice;
        item.setAttribute('aria-checked', selected ? 'true' : 'false');
        item.classList.toggle('survey-choice--selected', selected);
      });

      const error = step.querySelector('[data-survey-error]');
      if (error) error.hidden = true;

      this.answers[step.dataset.key] = {
        question: step.dataset.title || '',
        choice_id: choice.dataset.choiceId,
        value: choice.dataset.value || '',
        points: Number(choice.dataset.points || 0),
      };

      this.updateChrome();
      this.save();

      this.emit('answer', {
        step_id: step.dataset.stepId,
        question: step.dataset.title || '',
        answer: choice.dataset.value || '',
        points: Number(choice.dataset.points || 0),
        score: this.score,
      });

      if (step.dataset.autoAdvance === 'true') {
        setTimeout(() => this.advance(step, choice), 220);
      }
    }

    submitQuestion(step) {
      const choice = step.querySelector('[data-choice][aria-checked="true"]');

      if (!choice && step.dataset.required === 'true') {
        const error = step.querySelector('[data-survey-error]');
        if (error) {
          error.hidden = false;
          error.focus?.();
        }
        return;
      }

      this.advance(step, choice);
    }

    /* ---------------------------------------------------------------- events */

    handleClick = (event) => {
      const choice = event.target.closest('[data-choice]');
      if (choice && this.contains(choice)) {
        event.preventDefault();
        this.start();
        this.select(choice);
        return;
      }

      const next = event.target.closest('[data-survey-next]');
      if (next && this.contains(next)) {
        event.preventDefault();
        this.start();
        const step = next.closest('[data-step]');
        if (!step) return;
        if (step.dataset.stepType === 'question') this.submitQuestion(step);
        else this.advance(step, null);
        return;
      }

      const submit = event.target.closest('[data-survey-submit]');
      if (submit && this.contains(submit)) {
        event.preventDefault();
        const step = submit.closest('[data-step]');
        if (step) this.submitContact(step);
        return;
      }

      const cta = event.target.closest('[data-survey-cta]');
      if (cta && this.contains(cta)) {
        this.emit('result_click', {
          result_id: cta.closest('[data-step]')?.dataset.stepId,
          label: cta.textContent.trim(),
          url: cta.getAttribute('href'),
          score: this.score,
        });
      }
    };

    handleKeydown = (event) => {
      // Enter in the email field, where a form would normally submit for us.
      if (event.key === 'Enter' && event.target.closest('[data-survey-email]')) {
        event.preventDefault();
        const step = event.target.closest('[data-step]');
        if (step) this.submitContact(step);
        return;
      }

      const choice = event.target.closest('[data-choice]');
      if (!choice) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        this.select(choice);
        const step = choice.closest('[data-step]');
        if (step && step.dataset.autoAdvance !== 'true') this.advance(step, choice);
        return;
      }

      const offset = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (!offset) return;

      const choices = Array.from(choice.parentElement.querySelectorAll('[data-choice]'));
      const target = choices[(choices.indexOf(choice) + offset + choices.length) % choices.length];
      if (!target) return;

      event.preventDefault();
      target.focus();
      this.select(target);
    };

    /** Typeform-style A/B/C/D shortcuts, ignored while typing. */
    handleShortcut = (event) => {
      if (!this.current || this.current.dataset.stepType !== 'question') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const letter = event.key.toUpperCase();
      if (!/^[A-Z]$/.test(letter)) return;

      const choices = Array.from(this.current.querySelectorAll('[data-choice]'));
      const choice = choices[letter.charCodeAt(0) - 65];
      if (!choice) return;

      event.preventDefault();
      this.start();
      this.select(choice);
    };

    start() {
      if (this.started) return;
      this.started = true;
      this.emit('start', { survey: this.dataset.surveyName || '' });
    }

    /* ------------------------------------------------------------ completion */

    async submitContact(step) {
      if (this.submitting) return;

      const button = step.querySelector('[data-survey-submit]');
      const error = step.querySelector('[data-survey-error]');
      const email = step.querySelector('[data-survey-email]');
      const consent = step.querySelector('[data-survey-consent]');

      if (error) error.hidden = true;

      if (!email || !email.checkValidity()) {
        this.showFormError(error, step.dataset.emailError || 'Please enter a valid email address.');
        email?.focus();
        return;
      }

      if (consent && consent.required && !consent.checked) {
        this.showFormError(error, step.dataset.consentError || 'Please tick the box to continue.');
        consent.focus();
        return;
      }

      this.submitting = true;
      const outcome = this.outcome();
      const tags = [step.dataset.tags, outcome?.dataset.tag].filter(Boolean).join(',');

      button?.setAttribute('aria-busy', 'true');
      button?.setAttribute('disabled', 'disabled');

      // Shopify and Klaviyo are independent destinations — send to both at
      // once so the visitor waits for one round trip, not several.
      const deliveries = this.sendToKlaviyo(email.value, outcome, consent);

      if (step.dataset.createCustomer !== 'false') {
        deliveries.push(this.createCustomer(email.value, tags, consent));
      }

      await Promise.all(deliveries);

      button?.removeAttribute('aria-busy');
      this.updateConsentGate(step);
      this.submitting = false;

      this.emit('lead', {
        step_id: step.dataset.stepId,
        score: this.score,
        outcome_id: outcome?.dataset.stepId || '',
      });

      this.history.push(step.dataset.stepId);
      this.finish(outcome);
    }

    /**
     * Post the email to Shopify's own /contact endpoint — byte for byte the
     * request a newsletter form makes — which creates the customer and
     * subscribes them.
     *
     * A failure here must never trap the visitor before their result, so it is
     * logged rather than surfaced. `_fd=0` keeps Shopify from bouncing the
     * request to the primary domain, which on a *.myshopify.com preview turns
     * into a cross-origin redirect the browser refuses.
     */
    async createCustomer(email, tags, consent) {
      const body = new URLSearchParams({
        form_type: 'customer',
        utf8: '✓',
        'contact[email]': email,
      });

      if (tags) body.set('contact[tags]', tags);
      if (consent?.checked) body.set('contact[accepts_marketing]', 'true');

      try {
        const response = await fetch('/contact?_fd=0', {
          method: 'POST',
          body,
          headers: {
            Accept: 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });

        if (!response.ok) {
          console.warn(`[survey] Email capture returned ${response.status}`);
        }
      } catch (error) {
        console.warn('[survey] Email capture request failed', error);
      }
    }

    showFormError(node, message) {
      if (!node) return;
      node.textContent = message;
      node.hidden = false;
    }

    outcome() {
      const score = this.score;

      const match = this.results.find((result) => {
        const min = result.dataset.min === '' ? -Infinity : Number(result.dataset.min);
        const max = result.dataset.max === '' ? Infinity : Number(result.dataset.max);
        return score >= min && score <= max;
      });

      return match || this.results[this.results.length - 1] || null;
    }

    finish(preferred) {
      const outcome = preferred || this.outcome();

      if (!outcome) {
        console.warn('[survey] No result block matches — add one that covers this score.');
        return;
      }

      // The result is kept in session storage too, so a reload lands the
      // visitor back on their result rather than at the start.
      this.show(outcome);

      this.emit('complete', {
        score: this.score,
        outcome_id: outcome.dataset.stepId,
        outcome_name: outcome.dataset.name || outcome.dataset.title || '',
        answers: this.answerSummary(),
      });
    }

    answerSummary() {
      return Object.keys(this.answers).reduce((summary, key) => {
        summary[key] = this.answers[key].value;
        return summary;
      }, {});
    }

    /* ------------------------------------------------------------- analytics */

    emit(name, payload) {
      const detail = { survey_id: this.dataset.surveyId, ...payload };
      const prefix = this.dataset.eventPrefix || 'survey';

      this.dispatchEvent(new CustomEvent(`${prefix}:${name}`, { detail, bubbles: true }));

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `${prefix}_${name}`, ...detail });

      if (window.Shopify?.analytics?.publish) {
        try {
          window.Shopify.analytics.publish(`${prefix}_${name}`, detail);
        } catch (error) {
          // Custom pixel not listening — nothing to do.
        }
      }
    }

    /**
     * Every profile property this survey writes, keyed by its Klaviyo name.
     *
     * Names are prefixed with the survey's own prefix so a second survey on the
     * site can't overwrite the first, and so a generic answer key like
     * "timeline" can't collide with unrelated profile data.
     */
    klaviyoProperties(outcome) {
      const prefix = this.dataset.eventPrefix || 'survey';
      const name = (key) => `${prefix}_${key}`.replace(/[^a-z0-9_]+/gi, '_');

      const properties = {
        [name('name')]: this.dataset.surveyName || '',
        [name('score')]: this.score,
        [name('outcome')]: outcome?.dataset.name || outcome?.dataset.title || '',
        [name('outcome_id')]: outcome?.dataset.stepId || '',
        [name('completed_at')]: new Date().toISOString(),

        // Deliberately outside the prefix scheme: this is a fixed name that
        // existing Klaviyo segments match on, so it must not vary per survey.
        'Superquiz Score': this.score,
      };

      const answers = this.answerSummary();
      Object.keys(answers).forEach((key) => {
        properties[name(key)] = answers[key];
      });

      return properties;
    }

    /**
     * One call to Klaviyo's public Client API. Authenticated by the public API
     * key alone, so it runs safely from the browser with no private key and no
     * backend. Every endpoint answers 202 with an empty body.
     */
    klaviyoRequest(endpoint, payload) {
      const url = `https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(
        this.dataset.klaviyoCompany
      )}`;

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) {
            console.warn(`[survey] Klaviyo ${endpoint} returned ${response.status}`);
          }
        })
        .catch((error) => {
          // Klaviyo being unreachable must never cost the visitor their result.
          console.warn(`[survey] Klaviyo ${endpoint} request failed`, error);
        });
    }

    /**
     * Create the Klaviyo profile for this email, with the answers stored on it
     * as custom properties, and record the completion as an event.
     *
     * Talking to the API directly rather than through the onsite embed's
     * `klaviyo.push(...)` queue means results still arrive when that script is
     * disabled, slow or blocked — in which case the queue is never flushed and
     * the push is silently lost.
     *
     * The answers go out twice on purpose: on the profile, so segments can be
     * built from them later, and on the event, so a flow can branch on them the
     * moment the survey is completed.
     *
     * Returns the in-flight requests so the caller can await them.
     */
    sendToKlaviyo(email, outcome, consent) {
      if (this.dataset.klaviyo !== 'true') return [];

      if (!this.dataset.klaviyoCompany) {
        console.warn('[survey] Klaviyo is on but no public API key is set — skipping.');
        return [];
      }

      const properties = this.klaviyoProperties(outcome);
      const profile = { type: 'profile', attributes: { email, properties } };
      const requests = [this.klaviyoRequest('profiles', { data: profile })];

      // Consent is what subscribes someone; creating a profile never does.
      // The list's own opt-in settings decide single vs double opt-in.
      const list = this.dataset.klaviyoList;
      if (list && (!consent || consent.checked)) {
        requests.push(
          this.klaviyoRequest('subscriptions', {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.surveyName || 'Survey',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          })
        );
      }

      requests.push(
        this.klaviyoRequest('events', {
          data: {
            type: 'event',
            attributes: {
              properties,
              metric: {
                data: { type: 'metric', attributes: { name: this.dataset.klaviyoEvent || 'Completed Survey' } },
              },
              // Reusing the full profile resource, properties and all: if the
              // profile call above fails, the answers still land via the event.
              profile: { data: profile },
            },
          },
        })
      );

      // Ties the visitor's earlier onsite activity to this profile. Only worth
      // doing when the embed is actually loaded, and deliberately carries no
      // properties — the API calls above own that data.
      try {
        if (window.klaviyo && !Array.isArray(window.klaviyo)) {
          window.klaviyo.push(['identify', { $email: email }]);
        }
      } catch (error) {
        console.warn('[survey] Klaviyo onsite identify failed', error);
      }

      return requests;
    }

    /* ----------------------------------------------------------- theme editor */

    /** In the theme editor, selecting a step's block previews that step. */
    setupDesignMode() {
      if (!window.Shopify?.designMode) return;

      const reveal = (event) => {
        const step = event.target?.closest?.('[data-step]');
        if (step && this.contains(step)) this.show(step, { silent: true });
      };

      document.addEventListener('shopify:block:select', reveal);
      document.addEventListener('shopify:block:deselect', reveal);
    }
  }

  if (!customElements.get('survey-form')) {
    customElements.define('survey-form', SurveyForm);
  }
})();
